package br.com.vvrefeicoes.portal.feature.purchases

import br.com.vvrefeicoes.portal.core.util.spreadsheet.SpreadsheetFormatException
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportMonth
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportPayload
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportProduct
import br.com.vvrefeicoes.portal.domain.model.PurchasesType

private data class MonthPair(
    val month: Int,
    val unitPriceIndex: Int,
    val totalCostIndex: Int,
)

/**
 * Lê a aba "Fechamento de compras": A = produto, B = consumo, C = medição e, a partir de D,
 * pares (preço unitário, custo total) por mês. Os valores numéricos são mantidos como strings
 * para o backend fazer a conversão para centavos.
 */
internal fun parsePurchasesSpreadsheet(
    rows: List<List<String>>,
    type: PurchasesType,
    year: Int,
    targetMonth: Int,
): PurchasesImportPayload {
    val header = rows.firstOrNull() ?: throw SpreadsheetFormatException("Planilha vazia")
    val monthPairs = mutableListOf<MonthPair>()
    var index = 3
    while (index + 1 < header.size) {
        val month = monthNumber(header[index]) ?: break
        monthPairs += MonthPair(month, index, index + 1)
        index += 2
    }
    if (monthPairs.isEmpty()) {
        throw SpreadsheetFormatException("A planilha não tem colunas de meses")
    }
    val lastMonth = monthPairs.maxOf { it.month }
    if (targetMonth < 1 || targetMonth > lastMonth) {
        throw SpreadsheetFormatException("Não há dados para o período selecionado.")
    }

    val products = mutableListOf<PurchasesImportProduct>()
    rows.drop(1).forEach { row ->
        val name = row.getOrNull(0)?.trim().orEmpty()
        if (name.isEmpty()) return@forEach
        val measurement = row.getOrNull(2)?.trim().orEmpty()
        val months =
            monthPairs.mapNotNull { pair ->
                val unitPrice = row.getOrNull(pair.unitPriceIndex)?.trim().orEmpty()
                val totalCost = row.getOrNull(pair.totalCostIndex)?.trim().orEmpty()
                if (unitPrice.isEmpty() && totalCost.isEmpty()) {
                    null
                } else {
                    PurchasesImportMonth(
                        month = pair.month,
                        unitPrice = unitPrice,
                        totalCost = totalCost,
                    )
                }
            }
        if (months.isNotEmpty()) {
            products +=
                PurchasesImportProduct(
                    name = name,
                    measurement = measurement,
                    consumption = row.getOrNull(1)?.trim().orEmpty(),
                    months = months,
                )
        }
    }
    if (products.isEmpty()) {
        throw SpreadsheetFormatException("A planilha não tem produtos com dados")
    }
    if (products.none { product -> product.months.any { it.month == targetMonth } }) {
        throw SpreadsheetFormatException("Não há dados para o período selecionado.")
    }

    return PurchasesImportPayload(
        type = type,
        year = year,
        lastMonth = lastMonth,
        targetMonth = targetMonth,
        products = products,
    )
}

private fun monthNumber(value: String?): Int? {
    if (value == null) return null
    return MONTHS[normalize(value)]
}

private fun normalize(value: String): String =
    value
        .trim()
        .lowercase()
        .replace("á", "a")
        .replace("à", "a")
        .replace("ã", "a")
        .replace("â", "a")
        .replace("é", "e")
        .replace("ê", "e")
        .replace("í", "i")
        .replace("ó", "o")
        .replace("ô", "o")
        .replace("õ", "o")
        .replace("ú", "u")
        .replace("ç", "c")

private val MONTHS =
    mapOf(
        "janeiro" to 1,
        "fevereiro" to 2,
        "marco" to 3,
        "abril" to 4,
        "maio" to 5,
        "junho" to 6,
        "julho" to 7,
        "agosto" to 8,
        "setembro" to 9,
        "outubro" to 10,
        "novembro" to 11,
        "dezembro" to 12,
    )
